import { useEffect, useState } from "react";
import "./requestMultipleRailRatesPanel.css";
import type { MissingRailFreightSectionItem } from "../../../types/IMissingRailFreight";
import { icons } from "../../../svg";


type RequestMultipleRailRatesPanelProps = {
  open: boolean;
  origins: MissingRailFreightSectionItem[];
  requestedRailRateByOriginKey: Record<string, boolean>;
  onClose: () => void;
  onNext?: (selectedOrigins: MissingRailFreightSectionItem[]) => void;
};

const RequestMultipleRailRatesPanel = ({
  open,
  origins,
  requestedRailRateByOriginKey,
  onClose,
  onNext,
}: RequestMultipleRailRatesPanelProps) => {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedKeys(new Set());
    }
  }, [open]);

  if (!open) return null;

  const isOriginRequested = (origin: MissingRailFreightSectionItem) =>
    !!requestedRailRateByOriginKey[origin.originKey] ||
    (origin?.railValidation as any)?.railRateRequested === true;

  const selectableOrigins = origins.filter((origin) => !isOriginRequested(origin));

  const toggleOrigin = (key: string) => {
    const origin = selectableOrigins.find((item) => item.originKey === key);
    if (!origin) {
      return;
    }

    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCancel = () => {
    setSelectedKeys(new Set());
  };

  const handleNext = () => {
    const selected = selectableOrigins.filter((origin) =>
      selectedKeys.has(origin.originKey)
    );
    onNext?.(selected);
  };

  const selectedOrigins = selectableOrigins.filter((origin) =>
    selectedKeys.has(origin.originKey),
  );
  const allSelectedAreCompetitorOrigins =
    selectedOrigins.length > 0 &&
    selectedOrigins.every((origin) => origin.competitor === true);

  return (
    <div
      className="req-multiple-overlay"
      role="presentation"
    >
      <div
        className="req-multiple-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Request Rail Rates"
      >
        <div className="req-multiple-drag-handle" />

        <div className="req-multiple-header">
          <span className="req-multiple-title">Request Rail Rates</span>
          <button
            type="button"
            className="req-multiple-close-button"
            onClick={onClose}
            aria-label="Close"
          >
            <img
              src={icons.CloseBlackWhiteIcon}
              alt="Close"
              className="req-multiple-close-icon"
            />
          </button>
        </div>

        <p className="req-multiple-subtitle">
          Select one or more origins to request a rail rate
        </p>

        <div className="req-multiple-list">
          {selectableOrigins.map((origin) => {
            return (
              <div key={origin.originKey} className="req-multiple-item">
                <input
                  type="checkbox"
                  id={`req-multiple-origin-${origin.originKey}`}
                  className="req-multiple-checkbox"
                  checked={selectedKeys.has(origin.originKey)}
                  onChange={() => toggleOrigin(origin.originKey)}
                />
                <label
                  htmlFor={`req-multiple-origin-${origin.originKey}`}
                  className="req-multiple-item-label"
                >
                  {origin.originName}
                </label>
              </div>
            );
          })}
        </div>

        {selectedKeys.size > 0 && (
          <div className="req-multiple-footer">
            <button
              type="button"
              className="req-multiple-cancel-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="req-multiple-next-btn"
              onClick={handleNext}
            >
              {allSelectedAreCompetitorOrigins ? "Finish" : "Next"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestMultipleRailRatesPanel;
